// ---------------------------------------------------------------------------
// The product feature block.
//
// Markup mirrors the original design class-for-class and keeps the DOM id from
// the database, because connix-luxe.css targets several products by id
// (`#majestic-product .majestic-gallery`, `#veloura-product .veloura-gallery`).
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useState } from 'react';

import { useSite } from '../../context/SiteProvider.jsx';
import { useCart } from '../../context/CartProvider.jsx';
import { Reveal } from '../common/Reveal.jsx';
import { RichText } from '../common/RichText.jsx';

/** Two-image equipment galleries keep both photos; everything else swaps one. */
function ProductGallery({ product, selectedVariant }) {
  const { pick, language } = useSite();
  const pairImages = product.images.filter((image) => image.cssClass);

  if (pairImages.length) {
    return (
      <Reveal className={`product-gallery ${product.galleryClass ?? ''}`.trim()}>
        {pairImages.map((image) => (
          <img key={image.id} className={image.cssClass} src={image.url} alt={pick(image.alt)} />
        ))}
        {product.productNumber ? <span className="product-number">{product.productNumber}</span> : null}
      </Reveal>
    );
  }

  const source = selectedVariant?.imageUrl || product.coverImage;
  const label = selectedVariant ? pick(selectedVariant.label) : '';
  const alt = selectedVariant
    ? `${pick(product.name)} ${label}`.trim()
    : pick(product.coverAlt) || pick(product.name);

  return (
    <Reveal className={`product-gallery ${product.galleryClass ?? ''}`.trim()}>
      <img src={source} alt={alt} lang={language} />
      {product.productNumber ? <span className="product-number">{product.productNumber}</span> : null}
    </Reveal>
  );
}

function ProductDetails({ sections }) {
  const { pick } = useSite();

  const renderFlow = (items) =>
    items.map((item) => {
      const text = pick(item.text);
      if (item.kind === 'heading') return <h4 key={item.id}>{text}</h4>;
      if (item.kind === 'warning') return <p key={item.id} className="warning">{text}</p>;
      if (item.kind === 'ingredients') return <p key={item.id} className="ingredients">{text}</p>;
      return <p key={item.id}>{text}</p>;
    });

  return (
    <div className="product-details">
      {sections.map((section) => {
        const bullets = section.items.filter((item) => item.kind === 'bullet');
        const steps = section.items.filter((item) => item.kind === 'step');
        const flow = section.items.filter((item) => !['bullet', 'step'].includes(item.kind));

        // The imported manufacturer information has its own editorial treatment
        // in the stylesheet, so it renders as an article rather than a details.
        if (section.renderAs === 'article') {
          return (
            <article key={section.id} className="complete-product-information">
              <h3>{pick(section.title)}</h3>
              <div className="complete-product-copy">{renderFlow(flow)}</div>
            </article>
          );
        }

        return (
          <details key={section.id} open>
            <summary>{pick(section.title)}</summary>
            <div className="detail-copy">
              {renderFlow(flow)}
              {bullets.length ? (
                <ul>
                  {bullets.map((item) => <li key={item.id}>{pick(item.text)}</li>)}
                </ul>
              ) : null}
              {steps.length ? (
                <ol>
                  {steps.map((item) => <li key={item.id}>{pick(item.text)}</li>)}
                </ol>
              ) : null}
            </div>
          </details>
        );
      })}
    </div>
  );
}

export function ProductFeature({ product, isActive = false }) {
  const { t, pick, money } = useSite();
  const cart = useCart();

  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [customMl, setCustomMl] = useState(product.customSize?.minMl ?? 100);
  const [showSelectPrompt, setShowSelectPrompt] = useState(false);

  // A single-variant product (equipment, Therapy Liss) has nothing to choose.
  const isSingleVariant = product.variants.length === 1 && product.variants[0].unit === 'unit';

  useEffect(() => {
    setSelectedVariantId(null);
    setIsCustomOpen(false);
    setShowSelectPrompt(false);
    setCustomMl(product.customSize?.minMl ?? 100);
  }, [product.id, product.customSize?.minMl]);

  const selectedVariant = useMemo(
    () => product.variants.find((variant) => variant.id === selectedVariantId) ?? null,
    [product.variants, selectedVariantId],
  );

  const customOptions = useMemo(() => {
    const custom = product.customSize;
    if (!custom?.enabled) return [];
    const values = [];
    for (let ml = custom.minMl; ml <= custom.maxMl; ml += custom.stepMl) values.push(ml);
    return values;
  }, [product.customSize]);

  const customTotal = (product.customSize?.pricePerMl ?? 0) * customMl;

  const selectVariant = (variant) => {
    if (!variant.inStock) return;
    setSelectedVariantId(variant.id);
    setIsCustomOpen(false);
    setShowSelectPrompt(false);
  };

  const addSelected = () => {
    if (!selectedVariant) {
      setShowSelectPrompt(true);
      return;
    }
    cart.add({ productId: product.id, variantId: selectedVariant.id, isCustom: false, quantity: 1 });
    cart.open();
  };

  const addCustom = () => {
    cart.add({ productId: product.id, variantId: null, isCustom: true, customMl, quantity: 1 });
    cart.open();
  };

  // Equipment: no size grid, a single price and a direct add button.
  const addSingle = () => {
    const variant = product.variants[0];
    cart.add({ productId: product.id, variantId: variant.id, isCustom: false, quantity: 1 });
    cart.open();
  };

  const orderLabel = selectedVariant
    ? t('addToCart')
    : showSelectPrompt
      ? t('selectSizeFirst')
      : t('selectSizeFirst');

  const sectionClass = [
    'product-feature',
    product.featureClass,
    'section-pad',
    isActive ? 'is-active-product' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section id={product.domId} className={sectionClass}>
      <ProductGallery product={product} selectedVariant={selectedVariant} />

      <Reveal className="product-summary">
        {pick(product.eyebrow) ? <p className="eyebrow">{pick(product.eyebrow)}</p> : null}
        <RichText as="h2" html={pick(product.titleHtml) || pick(product.name)} />
        {pick(product.professionalTag) ? (
          <p className="professional-tag">{pick(product.professionalTag)}</p>
        ) : null}
        {pick(product.intro) ? <p>{pick(product.intro)}</p> : null}
        {product.tagline ? <p className="botox-tagline">{pick(product.tagline)}</p> : null}

        {product.audience.length ? (
          <ul className="product-audience">
            {product.audience.map((item) => <li key={item.id}>{pick(item.text)}</li>)}
          </ul>
        ) : null}

        {isSingleVariant ? (
          <>
            <div className="single-price">
              <span>{t('price')}</span>
              <strong>{money(product.variants[0].price)}</strong>
            </div>
            {!product.variants[0].inStock ? <p className="stock-note out">{t('outOfStock')}</p> : null}
            <div className="product-actions">
              <button
                className="button dark"
                type="button"
                onClick={addSingle}
                disabled={!product.variants[0].inStock}
              >
                <span>{product.variants[0].inStock ? t('addToCart') : t('outOfStock')}</span>
                <span>↗</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div
              className={`size-options ${product.sizeOptionsClass ?? ''}`.trim()}
              aria-label={`${pick(product.name)} size options`}
            >
              {product.variants.map((variant) => (
                <button
                  key={variant.id}
                  className={`size-option${variant.id === selectedVariantId ? ' selected' : ''}${
                    variant.inStock ? '' : ' is-unavailable'
                  }`}
                  type="button"
                  onClick={() => selectVariant(variant)}
                  disabled={!variant.inStock}
                  aria-pressed={variant.id === selectedVariantId}
                >
                  <span>{pick(variant.name) || pick(variant.label)}</span>
                  <strong>{variant.inStock ? money(variant.price) : t('outOfStock')}</strong>
                  {variant.inStock && variant.compareAtPrice ? (
                    <em className="offer-flag">
                      <s>{money(variant.compareAtPrice)}</s>
                      <b>{t('specialOffer')}</b>
                    </em>
                  ) : variant.lowStock ? (
                    <em className="stock-flag">{t('onlyLeft', { count: variant.stock })}</em>
                  ) : null}
                </button>
              ))}

              {product.customSize?.enabled ? (
                <button
                  className={`size-option custom-trigger${isCustomOpen ? ' selected' : ''}`}
                  type="button"
                  aria-expanded={isCustomOpen}
                  aria-controls={`${product.domId}-custom`}
                  onClick={() => {
                    setIsCustomOpen((open) => !open);
                    setSelectedVariantId(null);
                  }}
                >
                  <span>{t('makeSize')}</span>
                  <strong>{`${product.customSize.minMl}–${product.customSize.maxMl} ${product.customSize.unit}`}</strong>
                </button>
              ) : null}
            </div>

            {product.customSize?.enabled ? (
              <div id={`${product.domId}-custom`} className="custom-size-panel" hidden={!isCustomOpen}>
                <label htmlFor={`${product.domId}-custom-select`}>{t('chooseSize')}</label>
                <select
                  id={`${product.domId}-custom-select`}
                  value={customMl}
                  onChange={(event) => setCustomMl(Number(event.target.value))}
                >
                  {customOptions.map((value) => (
                    <option key={value} value={value}>{`${value} ${product.customSize.unit}`}</option>
                  ))}
                </select>
                <div>
                  <span>
                    {`${customMl} ${product.customSize.unit} × `}
                    {money(product.customSize.pricePerMl)}
                  </span>
                  <strong>{money(customTotal)}</strong>
                </div>
                <button className="button dark" type="button" onClick={addCustom}>
                  <span>{t('addToCart')}</span>
                  <span>↗</span>
                </button>
              </div>
            ) : null}

            <div className="product-actions">
              <button className="button dark" type="button" onClick={addSelected}>
                <span>{orderLabel}</span>
                <span>↗</span>
              </button>
            </div>
          </>
        )}
      </Reveal>

      <ProductDetails sections={product.sections} />
    </section>
  );
}
